import { getISOByParam } from 'iso-country-currency';
import keyword_extractor from 'keyword-extractor';
import { vars } from '../reactive';

export const ALPHABETS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
export const API_BOOKING_LENGTH = 90 as const;

export const RESEND_VERIFICATION_EMAIL_COUNTER = [0, 20000, 60000, 300000];

/**
 * @param text - The text to replace variables in
 * @param variables - The variables to replace
 * @returns The text with variables replaced
 */

export const replaceVariables = (text: string, variables: Record<string, string> = {}) => text.replace(/\{\{([^}]+)\}\}/g, (match, variable) => variables[variable] || '');

export const getKeys = Object.keys as <T extends {}>(obj: T) => Array<Extract<keyof T, string>>;

export const sentenceCase = (text: string) =>
  text
    .replace(/\w\S*/g, txt => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    })
    .replace(/\s+/g, ' ');

export const camelCase = (text: string) => {
  return text
    .replace(/\s(.)/g, function ($1) {
      return $1.toUpperCase();
    })
    .replace(/\s/g, '');
};

export const kebabCase = (text: string) => {
  return text.toLowerCase().replace(/[&+]/, 'and').replace(/ /g, '-');
};

export const camelToKebabCase = (text: string) => {
  return text.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
};

export const snakeCase = (text: string) => {
  return text.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
};

export const titleCase = (text: string) => {
  return text
    .replace(/\w\S*/g, txt => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    })
    .replace(/\s+/g, ' ');
};

export const upperCase = (text: string) => {
  return text.toUpperCase();
};

export const lowerCase = (text: string) => {
  return text.toLowerCase();
};

export const capitalize = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const pascalCase = (text: string) => {
  return text
    .replace(/\w\S*/g, txt => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    })
    .replace(/\s+/g, ' ');
};

export const clearLocationQueryParams = (location: Location) => {
  history.replaceState(null, null, location.pathname);
};

export const isiOS = () =>
  ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);

export const isAndroid = () => /Android|Opera Mini/i.test(navigator.userAgent);

export const getKeywords = (text: string) => {
  const keywords = keyword_extractor
    .extract(text, {
      language: 'english',
      remove_digits: true,
      return_changed_case: true
    })
    .filter(word => word.match(/^[a-zA-Z]+$/))
    .filter(word => word.length > 2);

  const counts: Record<string, number> = {};
  keywords.forEach(word => {
    counts[word] = counts[word] ? counts[word] + 1 : 1;
  });
  const sorted = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  return sorted.slice(0, 7);
};

export const extractFirstImageFromHTML = (html: string, siteUrl: string) => {
  return siteUrl + html.match(/\/static\/.*\.jpg/)?.[0];
};

export const JSONparse = (json: string | Record<string, any>) => {
  try {
    if (typeof json === 'object') {
      return json;
    }
    return JSON.parse(json);
  } catch (error) {
    return null;
  }
};

export const JSONstringify = (json: any) => {
  try {
    return JSON.stringify(json);
  } catch (error) {
    return null;
  }
};

export const clearLocalStorage = () => {
  console.warn('Clearing local storage');
  vars.selectedProduct(null);
  vars.selectedCustomizations({});
  vars.selectedOrderGroupSlots([{ slots: [] }]);
  vars.newOrders(null);
  vars.selectedPets([]);
  vars.recurrenceType('one_off');
};

export const checkProductSubscriptionsDependecies = (product: Queries.CollarAPI_Product, userSubscriptions: Queries.CollarAPI_OrderSubscription[]) => {
  return (
    !product?.SubscriptionProducts ||
    product?.SubscriptionProducts?.length === 0 ||
    product?.SubscriptionProducts?.some(subscriptionProduct =>
      (userSubscriptions || []).some(userSubscription => {
        const userSubscriptionProductId = userSubscription.OrderSubscriptionRepeats?.[0]?.OrderItem?.item?.id;
        const isCanceled = userSubscription.status === 'CANCELED';
        return !isCanceled && subscriptionProduct.id === userSubscriptionProductId;
      })
    )
  );
};

export type RequireAtLeastOne<T, Keys extends keyof T = keyof T> = Pick<T, Exclude<keyof T, Keys>> &
  {
    [K in Keys]-?: Required<Pick<T, K>> & Partial<Pick<T, Exclude<Keys, K>>>;
  }[Keys];

export const getBranchCurrencySymbol = () => {
  return vars.currentBranch()?.branchCurrency?.symbol || '£';
};

export const getISOByCountryName = (countryName: string) => {
  try {
    return getISOByParam('countryName', countryName);
  } catch (e) {
    console.log(e);
    return 'GB';
  }
};

export function encodeText(str: string) {
  return Array.from(str)
    .map(c => c.charCodeAt(0).toString(16).padStart(4, '0'))
    .join('');
}

export function decodeText(hex: string) {
  return hex
    .match(/.{1,4}/g)
    .map(h => String.fromCharCode(parseInt(h, 16)))
    .join('');
}
